import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';

// Formats a count with Indian digit grouping (e.g. 2131 -> "2,131").
// Returns null for a missing/invalid count so callers can omit it cleanly.
export const formatCount = (n) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return null;
  return n.toLocaleString('en-IN');
};

// Pure pagination-summary helper, kept outside the component so it can be
// unit-tested without rendering React. It never produces NaN: a missing or
// invalid totalItems/itemsPerPage/currentPage falls back to a safe default
// instead of propagating into arithmetic (which is how "Showing NaN to NaN
// of results" used to happen when a caller forgot to pass totalItems).
export const getPaginationSummary = ({ currentPage, itemsPerPage, totalItems, noun = 'results' } = {}) => {
  const perPage = (typeof itemsPerPage === 'number' && itemsPerPage > 0) ? itemsPerPage : 25;
  const page = (typeof currentPage === 'number' && currentPage > 0) ? currentPage : 1;
  const hasTotal = typeof totalItems === 'number' && !Number.isNaN(totalItems);

  if (hasTotal && totalItems === 0) {
    return { isEmpty: true, hasTotal: true, startItem: 0, endItem: 0, totalItems: 0, noun };
  }

  const startItem = (page - 1) * perPage + 1;
  const endItem = hasTotal ? Math.min(page * perPage, totalItems) : page * perPage;

  return { isEmpty: false, hasTotal, startItem, endItem, totalItems: hasTotal ? totalItems : null, noun };
};

const Pagination = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange, emptyLabel = 'No results match these filters', noun = 'results' }) => {
  const summary = getPaginationSummary({ currentPage, itemsPerPage, totalItems, noun });

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-slate-200">
      <p className="text-sm text-slate-600">
        {summary.isEmpty ? emptyLabel : (
          <>
            Showing <span className="font-medium">{summary.startItem}</span> to{' '}
            <span className="font-medium">{summary.endItem}</span>
            {summary.hasTotal && (
              <> of <span className="font-medium">{formatCount(summary.totalItems)}</span></>
            )}{' '}
            {summary.noun}
          </>
        )}
      </p>
      
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          data-testid="pagination-prev"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        
        {getPageNumbers().map((page, idx) => (
          page === '...' ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">...</span>
          ) : (
            <Button
              key={page}
              variant={currentPage === page ? 'default' : 'outline'}
              size="sm"
              onClick={() => onPageChange(page)}
              className="min-w-[32px]"
              data-testid={`pagination-page-${page}`}
            >
              {page}
            </Button>
          )
        ))}
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          data-testid="pagination-next"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
